import { evalPath } from './routes';

// Which suite each bespoke queue labels, and where that queue now lives.
//
// A queue is not a thing; it is a suite being labelled
// (`docs/eval-ui-structure-review.md` Part D), so the address is
// `/suite/<suite>/label` and there is no second grammar for the four surfaces
// that used to be applications of their own. The mapping lives here because
// four callers need it and none of them should own it: the routes that mount
// the queues, the redirects that keep the old links working, the suite page's
// **Label** action, and each queue's own cursor.
//
// **The case id is `<suite>/<item>` for every one of them.** That is not a
// convention this file invented — it is what the exporters write
// (`label_stores.py`, `rec_quality.py`), and the whole point of deriving the
// link rather than declaring it is that a page cannot drift from the record.
//
//   rec_video_grades/g3_ela_poetry_context/lOUnhSMGdOo
//   rec_quality/g3_math_multiplication
//   content_quality/lOUnhSMGdOo
//   thumbnail_appeal/lOUnhSMGdOo
//   within_topic/g3_ela_poetry_context/lOUnhSMGdOo
//
// So an id carries slashes and the tail after `label/` is rejoined, never read
// as one segment — `routes.seg` keeps them and `parsePath` puts them back.

export const REC_VIDEO_GRADES = 'rec_video_grades';
export const REC_QUALITY = 'rec_quality';
export const CONTENT_QUALITY = 'content_quality';
export const THUMBNAIL_APPEAL = 'thumbnail_appeal';
export const WITHIN_TOPIC = 'within_topic';

// The five queues, each naming the suite it writes and the address it used to
// answer to. `legacy` is not history for its own sake: those links are pasted
// in tickets, printed by the CLI and bookmarked, so every one of them still
// resolves and forwards.
export const QUEUES = [
    { suite: REC_VIDEO_GRADES, legacy: '/_eval/rec', item: '/video' },
    { suite: REC_QUALITY, legacy: '/_eval/rec/lesson', item: '' },
    { suite: CONTENT_QUALITY, legacy: '/_eval/content', item: '/video' },
    { suite: THUMBNAIL_APPEAL, legacy: '/_eval/thumbnail', item: '/video' },
    { suite: WITHIN_TOPIC, legacy: '/_eval/within_topic', item: '/video' },
];

export const QUEUE_SUITES = QUEUES.map((queue) => queue.suite);

/** Does this suite have a bespoke queue, or does it label on the generic one? */
export function hasQueue(suite) {
    return QUEUE_SUITES.includes(suite);
}

/**
 * The address of one item in a queue, or of the queue itself.
 *
 * `itemId` is the queue's own id — `<video>` or `<case>/<video>` — and the
 * suite prefix is added here rather than at five call sites, because a caller
 * that forgot it would produce a link to a case that does not exist.
 */
export function queueLabelPath(suite, itemId) {
    return evalPath.label(suite, itemId ? `${suite}/${itemId}` : null);
}

/**
 * The address of the queue's AUDIT — the cases it has already labelled.
 *
 * The same id arithmetic as `queueLabelPath` against the other of the two
 * labelling addresses. It exists because "re-check labeled cases" had nowhere
 * to go on a queue with a bespoke surface: the button drew an audit batch
 * through the generic review API and then forwarded to `/label`, which is the
 * DRAW — a queue whose corpus is fully decided draws nothing, so the labeler
 * was sent from "re-check my labels" to "there is nothing left to draw".
 */
export function queueLabeledPath(suite, itemId) {
    return evalPath.labeled(suite, itemId ? `${suite}/${itemId}` : null);
}

/**
 * The queue's own id, back out of a case id.
 *
 * Tolerant of a caller that passes the item id already: the prefix is stripped
 * when it is there and the value is returned untouched when it is not, so a
 * hand-typed `/label/lOUnhSMGdOo` resolves to the item rather than to nothing.
 */
export function queueItemId(suite, caseId) {
    if (!caseId) return null;
    const prefix = `${suite}/`;
    return caseId.startsWith(prefix) ? caseId.slice(prefix.length) : caseId;
}

/**
 * Where an address these queues used to answer to lives now, or null.
 *
 * `/_eval/thumbnail/video/vid2` → `/_eval/all/suite/thumbnail_appeal/label/thumbnail_appeal/vid2`
 *
 * A function rather than a route component's inline arithmetic, because it is
 * the part that can be wrong quietly: a redirect that drops the item forwards a
 * bookmark to the top of a 913-item queue and looks like it worked. The longest
 * legacy base wins, so `/_eval/rec/lesson` is not swallowed by `/_eval/rec`.
 */
export function legacyLabelPath(pathname) {
    const matches = QUEUES
        .filter((queue) => pathname === queue.legacy
            || pathname.startsWith(`${queue.legacy}/`))
        .sort((a, b) => b.legacy.length - a.legacy.length);
    const queue = matches[0];
    if (!queue) return null;
    const tail = pathname
        .slice(queue.legacy.length)
        .replace(/^\/video\//, '')
        .replace(/^\/+|\/+$/g, '');
    return queueLabelPath(queue.suite, tail ? decodeURIComponent(tail) : null);
}
